/**
 * Descarga un fichero de forma compatible con navegadores de escritorio y móvil
 * (iOS Safari, Android Chrome, etc.).
 *
 * `file-saver` usa internamente `<a download>` + `URL.createObjectURL`, pero iOS
 * Safari ignora el atributo `download` en muchos casos y los data-URIs grandes
 * directamente no funcionan en móvil. Esta utilidad:
 *
 * 1. Acepta un Blob/File o un string base64 (con MIME type correcto).
 * 2. Intenta abrir una nueva ventana/pestaña con la URL del blob, lo que dispara
 *    el diálogo nativo de descarga/compartir en iOS.
 * 3. Usa `<a download>` como primera opción en navegadores que lo soportan bien.
 */

/** Detecta si el user-agent es iOS (iPhone / iPad / iPod). */
const isIOS = (): boolean =>
  /iP(hone|ad|od)/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

/** Detecta si estamos en un navegador Android. */
const isAndroid = (): boolean => /Android/i.test(navigator.userAgent)

/**
 * Convierte un string base64 crudo (sin prefijo data:) a un Blob.
 */
function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteChars = atob(base64)
  const byteArrays: Uint8Array[] = []

  // Procesar en trozos de 512 bytes para evitar picos de memoria
  for (let offset = 0; offset < byteChars.length; offset += 512) {
    const slice = byteChars.slice(offset, offset + 512)
    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }
    byteArrays.push(new Uint8Array(byteNumbers))
  }

  return new Blob(byteArrays, { type: mimeType })
}

/**
 * Infiere el MIME type correcto a partir de la extensión del nombre de archivo.
 */
function mimeFromFilename(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    case 'xls':
      return 'application/vnd.ms-excel'
    case 'csv':
      return 'text/csv'
    case 'pdf':
      return 'application/pdf'
    case 'png':
      return 'image/png'
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    default:
      return 'application/octet-stream'
  }
}

/**
 * Descarga un archivo de forma compatible con todos los navegadores, incluyendo
 * iOS Safari y Android Chrome.
 *
 * @param data - Blob, File, o string base64 crudo (sin prefijo `data:`)
 * @param filename - Nombre con el que se guardará el archivo (incluir extensión)
 * @param mimeType - MIME type opcional. Si no se pasa, se infiere de la extensión.
 */
export function downloadFile(
  data: Blob | File | string,
  filename: string,
  mimeType?: string,
): void {
  const resolvedMime = mimeType || mimeFromFilename(filename)

  // Convertir base64 string a Blob si es necesario
  const blob: Blob =
    typeof data === 'string' ? base64ToBlob(data, resolvedMime) : data

  const url = URL.createObjectURL(blob)

  if (isIOS()) {
    // iOS Safari: abrir en nueva pestaña para que el sistema muestre el diálogo
    // nativo de "Abrir en..." / "Guardar en Archivos". El atributo `download` es
    // ignorado en la mayoría de versiones de Safari móvil.
    const win = window.open(url, '_blank')
    if (!win) {
      // Si el popup fue bloqueado, redirigir la pestaña actual
      window.location.href = url
    }
    // Revocar después de un tiempo para que Safari tenga tiempo de procesar
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } else {
    // Desktop y Android Chrome: usar un <a> con download
    const link = document.createElement('a')
    link.href = url
    link.download = filename

    // Android Chrome necesita que el enlace esté en el DOM
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()

    // Limpiar
    setTimeout(() => {
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }, isAndroid() ? 5_000 : 200)
  }
}
